import React, { FunctionComponent, useEffect, useState } from "react";
import HeaderPanel from "../../layout/HeaderPanel";
import ChatBot from "../../generic/chat/ChatBot";
import { AskAiProps, useAskAiModal } from "./AskAiModalHook";
import { AskAiReviewSaveModal } from "./AskAiReviewSaveModal";
import { contentStyles } from "../../layout/ActionModal";
import { Icon, Modal, TooltipHost } from "@fluentui/react";
import {
  formatMessage,
  formatMessageDefault,
  formatMessageWithParamsDefault,
  getPreferredLanguage,
} from "../../../utils/helpers/LanguageHelper";
import { ArrowCounterclockwiseRegular } from "@fluentui/react-icons";
import {
  ASK_AI_STATUS_CAPTION_DEFAULTS,
  CAPTION_ASKAI_DOCUMENT_SENT_ON,
  CAPTION_ASKAI_INDEX_COMPLETED,
  CAPTION_ASKAI_INDEX_IN_PROGRESS,
  CAPTION_BASE_WAITING,
} from "./validationCaptions";
import { RagIndexStatusValue } from "../../../api/document/RagIndexing";

const SUCCESS_BANNER_DURATION_MS = 10_000;

function getBannerDetails(
  status: RagIndexStatusValue | null,
  uploadObserved: boolean
): { text: string; color: string } | null {
  if (status === "IN_PROGRESS") {
    return {
      text: formatMessageDefault(
        CAPTION_ASKAI_INDEX_IN_PROGRESS,
        ASK_AI_STATUS_CAPTION_DEFAULTS[CAPTION_ASKAI_INDEX_IN_PROGRESS].en
      ),
      color: "#ea8f00",
    };
  }

  if (uploadObserved && (status === "SUCCESS" || status === "FAILED" || status === "TIMEOUT")) {
    return {
      text: formatMessageDefault(
        CAPTION_ASKAI_INDEX_COMPLETED,
        ASK_AI_STATUS_CAPTION_DEFAULTS[CAPTION_ASKAI_INDEX_COMPLETED].en
      ),
      color: "var(--color-green)",
    };
  }

  return null;
}

function getDocumentSentTooltip(sentAt: string | null): string | null {
  if (!sentAt) {
    return null;
  }

  const sentDate = new Date(sentAt);
  if (Number.isNaN(sentDate.getTime())) {
    return null;
  }

  const locale = getPreferredLanguage();
  const date = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(sentDate);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(sentDate);

  return formatMessageWithParamsDefault(
    CAPTION_ASKAI_DOCUMENT_SENT_ON,
    ASK_AI_STATUS_CAPTION_DEFAULTS[CAPTION_ASKAI_DOCUMENT_SENT_ON].en,
    { date, time }
  );
}

export const AskAiModal: FunctionComponent<AskAiProps> = (props) => {
  const {
    handleBack,
    messages,
    loading,
    onMessageSend,
    hasStagedReviewData,
    showReviewSaveModal,
    onReviewModalClose,
    onReviewSave,
    resetChat,
    ragIndexStatus,
    ragIndexSentAt,
    ragIndexUploadObserved,
  } = useAskAiModal(props);
  const [isSuccessBannerVisible, setIsSuccessBannerVisible] = useState(true);

  useEffect(() => {
    if (ragIndexStatus !== "SUCCESS" || !ragIndexUploadObserved) {
      setIsSuccessBannerVisible(true);
      return;
    }

    setIsSuccessBannerVisible(true);
    const timeoutId = window.setTimeout(() => setIsSuccessBannerVisible(false), SUCCESS_BANNER_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [ragIndexStatus, ragIndexUploadObserved]);

  const banner = getBannerDetails(ragIndexStatus, ragIndexUploadObserved);
  const shouldShowBanner = banner && (ragIndexStatus !== "SUCCESS" || isSuccessBannerVisible);
  const documentSentTooltip = getDocumentSentTooltip(ragIndexSentAt);
  const infoIconTooltip = documentSentTooltip ?? formatMessageDefault(CAPTION_BASE_WAITING, "Waiting...");

  return (
    <>
      <Modal
        titleAriaId={"titleId"}
        isOpen={props.isShown}
        containerClassName={contentStyles.container}
        styles={{ scrollableContent: { display: "flex", flexDirection: "column" } }}
      >
        <HeaderPanel
          data-testid={"header"}
          applyPadding
          enableBackArrow
          onBackArrowClick={handleBack}
          headerRowJustifyContent="left"
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              padding: "1px 0",
            }}
          >
            <span>{formatMessage("cof.base.askai")}</span>
            <TooltipHost content={infoIconTooltip}>
              <span
                aria-label={infoIconTooltip}
                data-testid="ask-ai-info-icon"
                style={{ display: "inline-flex", marginLeft: "4px", cursor: "default" }}
              >
                <Icon iconName="InfoSolid" style={{ color: "var(--color-blue)", fontSize: "14px" }} />
              </span>
            </TooltipHost>
          </span>
          <button
            data-testid="reset-chat-button"
            onClick={resetChat}
            style={{
              border: "1px solid #007ac3",
              background: "transparent",
              borderRadius: "4px",
              padding: "0 6px",
              color: "#007ac3",
              font: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              cursor: "pointer",
              marginLeft: "8px",
              fontSize: "12px",
            }}
          >
            <ArrowCounterclockwiseRegular style={{ fontSize: "12px", marginTop: "2px" }} />
            <span style={{ marginLeft: "2px", display: "inline-block", lineHeight: "14px", marginBottom: "2px" }}>
              {formatMessageDefault("cof.validation.button.resetchat", "reset chat")}
            </span>
          </button>
        </HeaderPanel>

        {shouldShowBanner && (
          <div
            data-testid="rag-index-status-banner"
            style={{
              boxSizing: "border-box",
              margin: "8px 8px 0",
              padding: "10px 16px",
              backgroundColor: banner.color,
              color: "white",
              borderRadius: "6px",
            }}
          >
            {banner.text}
          </div>
        )}

        <ChatBot
          placeholder={formatMessage("cof.validation.placeholder.chatrequest")}
          disclaimer={formatMessageDefault(
            "cof.base.askai.disclaimer",
            "Responsive AI can make mistakes. Verify critical information for accuracy."
          )}
          disabled={loading}
          loading={loading}
          messages={messages}
          onMessageSend={onMessageSend}
        />
      </Modal>
      {props.isShown && hasStagedReviewData && showReviewSaveModal && (
        <AskAiReviewSaveModal
          isShown={showReviewSaveModal}
          onHide={onReviewModalClose}
          onSave={onReviewSave}
          documentId={props.documentId}
          coObjects={props.coObjects}
          existingRules={props.rules}
          availableTags={props.tags}
        />
      )}
    </>
  );
};
